import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  getAdaptiveRecommendation,
  addAdaptiveRecommendation,
  updateAdaptiveRecommendation,
  deleteAdaptiveRecommendation
} from '../../api/adaptiveCoach'
import { editProgramWorkout, deleteProgramWorkout } from '../../api/program'
import { Toast, Dialog, Swiper, Button } from 'antd-mobile'
import moment from 'moment'
import SleepCard from './CardComponents/SleepCard'
import EditRecommendationModal from './Modals/EditRecommendationModal'
import ExerciseSummaryModal from './WorkoutDetails/ExerciseSummaryModal'
import ExerciseIcon from '../../asserts/icons/physical-assessment/ExerciseIcon.svg'
import SleepIcon from '../../asserts/icons/physical-assessment/sleep_icon.svg'
import './styles/ExerciseCard.css'
import { loadUser, saveUser, setTokenItem, getToken } from '../../utils'
import ExerciseCard from './CardComponents/ExerciseCard'
import { getMe } from '../../api'
import { getLiveProgram, getWorkoutDetail } from '../../api/program'
import ProgramWorkoutDetailModal from './WorkoutDetails/ProgramWorkoutDetailModal'
import EditProgramModal from './Modals/EditProgramModal'
import NativeHSD from '../public/healthySignal/native'
import {
  getCachedRecommendations,
  setCachedRecommendations,
  getCachedLiveProgram,
  setCachedLiveProgram,
  clearProgramsCache
} from '../../utils/programsCache'

function Programs() {
  const { hash } = useParams()
  const [exerciseByDay, setExerciseByDay] = useState({})
  const [sleepByDay, setSleepByDay] = useState({})
  const [loading, setLoading] = useState(true)
  const [exerciseSummary, setExerciseSummary] = useState(null)
  const [sleepSummary, setSleepSummary] = useState(null)
  const [editingActivity, setEditingActivity] = useState(null)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [isViewOnly, setIsViewOnly] = useState(false)
  const [isAddMode, setIsAddMode] = useState(false)
  const [isManualsave, setIsManualsave] = useState(false)
  const scrollContainerRef = useRef(null)
  const todayDate = useMemo(() => moment().format('YYYY-MM-DD'), [])
  const [user, setUser] = useState(() => {
    if (navigator.userAgent.includes('life.apeiron.ios')) {
      return null
    } else {
      const user = loadUser()
      return user
    }
  })
  const todayIndex = moment().isoWeekday() - 1
  const [activeDayIndex, setActiveDayIndex] = useState(todayIndex)
  const daysToShow = [0, 1, 2, 3, 4, 5, 6] // Always show full week
  const isFetchingRef = useRef(false)
  const [direction, setDirection] = useState('right')
  const prevIndexRef = useRef(todayIndex)
  const swiperRef = useRef(null)
  const [programItems, setProgramItems] = useState([])
  const [showProgramModal, setShowProgramModal] = useState(false)
  const [showEditProgramModal, setShowEditProgramModal] = useState(false)
  const [programWorkoutDetail, setProgramWorkoutDetail] = useState(null)
  const [plannedData, setPlannedData] = useState([])
  const [wasUpdatedInModal, setWasUpdatedInModal] = useState(false)

  const refreshPerson = async (token) => {
    setTokenItem(token)
    const { person } = await getMe()
    setUser(person)
    saveUser(person)
  }

  useEffect(() => {
    window.handleMessageFromNative = (token) => {
      refreshPerson(token)
    }
    if (navigator.userAgent.includes('life.apeiron.ios')) {
      // Check if we already have a token and user data before requesting
      if (!getToken() || !loadUser()) {
        try {
          window.webkit?.messageHandlers?.loadUser?.postMessage({})
        } catch (err) {
          console.error('🚨 Failed to request token from Swift:', err)
        }
      }
    }
  }, [])
  
  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    let startY = 0
    let isPullingDown = false

    const onTouchStart = (e) => {
      if (container.scrollTop === 0) {
        startY = e.touches[0].clientY
        isPullingDown = true
      }
    }

    const onTouchMove = (e) => {
      if (!isPullingDown) return

      const currentY = e.touches[0].clientY
      const distance = currentY - startY

      if (distance > 80 && !isFetchingRef.current) {
        isFetchingRef.current = true
        fetchRecommendations().finally(() => {
          isFetchingRef.current = false
        })
        isPullingDown = false
      }
    }

    const onTouchEnd = () => {
      isPullingDown = false
    }

    container.addEventListener('touchstart', onTouchStart)
    container.addEventListener('touchmove', onTouchMove)
    container.addEventListener('touchend', onTouchEnd)

    return () => {
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
    }
  }, [loading])

  const fetchAdaptiveRecommendations = async () => {
    const personId = user?.id
    
    try {
      const response = await getAdaptiveRecommendation(personId, todayDate)
      
      const combined = response?.combined_recommendation ?? []
      const exerciseMap = {}
      const sleepMap = {}

      combined.forEach((item) => {
        const day = item?.day_of_week
        if (day === undefined || day === null) return

        const isSleep = item?.isSleepActivity
        if (isSleep) {
          if (!sleepMap[day]) sleepMap[day] = []
          sleepMap[day].push(item)
        } else {
          if (!exerciseMap[day]) exerciseMap[day] = []
          exerciseMap[day].push(item)
        }
      })

      setExerciseByDay(exerciseMap)
      setSleepByDay(sleepMap)
      setExerciseSummary(response?.exercise_summary ?? null)
      setSleepSummary(response?.sleep_recommendation ?? null)
    } catch (error) {
      console.error('Error fetching adaptive recommendations:', error)
    }
  }
  
  const fetchLivePrograms = async () => {
    const personId = user?.id
    const weekRange = `${moment().startOf('isoWeek').format('YYYY-MM-DD')}-${moment().endOf('isoWeek').format('YYYY-MM-DD')}`
    
    const liveProgram = await getLiveProgram(
      personId,
      moment().startOf('isoWeek').format('YYYY-MM-DD'),
      moment().endOf('isoWeek').format('YYYY-MM-DD')
    )

    setProgramItems(liveProgram?.exercise?.summary?.workouts ?? [])
    setPlannedData(liveProgram?.exercise?.program?.items ?? [])
  }
  
  const fetchRecommendations = async () => {
    setLoading(true)

    try {
      await fetchAdaptiveRecommendations()
      await fetchLivePrograms()
    } catch (error) {
      console.error('Error fetching recommendations:', error)
      Toast.show({ icon: 'fail', content: 'Failed to fetch recommendations' })
    } finally {
      Toast.clear()
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log(user, 'user in program')
    if (user) {
      fetchRecommendations()
    } else {
      // Try to load user from localStorage if not available
      const storedUser = loadUser()
      if (storedUser) {
        setUser(storedUser)
      } else {
        console.log('No user found in localStorage')
      }
    }
  }, [todayDate, user])

  const handleEdit = (activity) => {
    if (!activity) return
    setEditingActivity(activity)
    setEditModalVisible(true)
    setIsManualsave(false)
    setIsViewOnly(false)
  }

  const handleSaveEdit = async (updatedActivity) => {
    try {
      const personId = user?.id

      Toast.show({
        icon: 'loading',
        content: isAddMode ? 'Adding...' : 'Updating...',
        duration: 0
      })

      if (isManualsave) {
        const id = updatedActivity?.id
        const durationInSeconds = parseInt(updatedActivity?.duration || 0, 10) * 60
        const payload = [
          {
            id,
            type: updatedActivity?.type,
            record_date: updatedActivity?.record_date,
            rpe_manual: updatedActivity?.rpe_manual ?? 1,
            pillar: updatedActivity?.pillar,
            duration: durationInSeconds,
            activity_type_shown: updatedActivity?.activity_type_shown
          }
        ]

        await editProgramWorkout(personId, payload)
        
        clearProgramsCache(personId)
        await fetchLivePrograms()

        setIsManualsave(false)
        setShowEditProgramModal(false)

        Toast.show({ icon: 'success', content: 'Workout updated successfully' })
      } else {
        if (isAddMode) {
          await addAdaptiveRecommendation(personId, todayDate, updatedActivity)
        } else {
          await updateAdaptiveRecommendation(
            personId,
            todayDate,
            updatedActivity.id,
            updatedActivity
          )
        }

        clearProgramsCache(personId)
        
        await fetchAdaptiveRecommendations()

        setIsAddMode(false)
        setShowEditProgramModal(false)

        Toast.show({
          icon: 'success',
          content: isAddMode
            ? 'Recommendation added successfully'
            : 'Recommendation updated successfully'
        })
      }

      setEditModalVisible(false)
      setEditingActivity(null)
    } catch (err) {
      console.error('Error saving:', err)
      Toast.show({
        icon: 'fail',
        content: isAddMode
          ? 'Failed to add'
          : 'Failed to update'
      })
    } finally {
      Toast.clear()
    }
  }
  
  const programItemsByDay = useMemo(() => {
    const map = {}
    const startOfWeek = moment().startOf('isoWeek')

    programItems.forEach((item) => {
      const recordDate = moment(item.record_date)
      const dayIndex = recordDate.diff(startOfWeek, 'days')

      if (dayIndex >= 0 && dayIndex < 7) {
        if (!map[dayIndex]) map[dayIndex] = []
        map[dayIndex].push(item)
      }
    })

    return map
  }, [programItems])  

  const handleView = (activity) => {
    setEditingActivity(activity)
    setEditModalVisible(true)
    setIsViewOnly(true)
  }

  const handleAddWorkout = () => {
    setEditingActivity(null)
    setIsViewOnly(false)
    setIsAddMode(true)
    setEditModalVisible(true)
  }

  const handleIndexChange = (index) => {
    const newDay = daysToShow[index]
    setDirection(newDay > prevIndexRef.current ? 'right' : 'left')
    setActiveDayIndex(newDay)
    prevIndexRef.current = newDay
  }  

  const handleHeaderClick = (newIndex) => {
    if (newIndex < 0 || newIndex > 6) return

    setDirection(newIndex > activeDayIndex ? 'right' : 'left')
    setActiveDayIndex(newIndex)
    prevIndexRef.current = newIndex

    const swiperSlideIndex = daysToShow.indexOf(newIndex)
    if (swiperSlideIndex !== -1) {
      swiperRef.current?.swipeTo(swiperSlideIndex)
    }
  }

  const handleDelete = async (activity) => {
    if (!activity || !activity.id) return

    const confirmed = await Dialog.confirm({
      title: 'Delete program item',
      content: 'Do you want to delete this item?',
      cancelText: 'Cancel',
      confirmText: 'Confirm'
    })

    if (!confirmed) return

    const personId = user?.id
    const recommendationId = activity.id
    const dayOfWeek = activity.day_of_week

    const activityDate = moment()
      .startOf('isoWeek')
      .add(dayOfWeek, 'days')
      .format('YYYY-MM-DD')

    try {
      Toast.show({
        icon: 'loading',
        content: 'Deleting recommendation...',
        duration: 0
      })

      if (isManualsave) {        
        const payload = [{ id: recommendationId, is_delete: true }]
        await deleteProgramWorkout(personId, payload)
        
        clearProgramsCache(personId)
        await fetchLivePrograms()
        setIsManualsave(false)
        setShowEditProgramModal(false)
        Toast.show({ icon: 'success', content: 'Recommendation deleted' })
      } else {
        await deleteAdaptiveRecommendation(
          personId,
          activityDate,
          recommendationId
        )
        
        clearProgramsCache(personId)
      }

      Toast.clear()
      Toast.show({ icon: 'success', content: 'Recommendation deleted' })

      setExerciseByDay((prev) => {
        const updated = { ...prev }
        if (updated[dayOfWeek]) {
          updated[dayOfWeek] = updated[dayOfWeek].filter(
            (item) => item.id !== recommendationId
          )
        }
        return updated
      })

      setEditModalVisible(false)
      setEditingActivity(null)
      setIsAddMode(false)
    } catch (err) {
      console.error('Delete failed:', err)
      Toast.clear()
      Toast.show({ icon: 'fail', content: 'Failed to delete' })
    }
  }

  const handleViewProgramItem = async (activity) => {
    setProgramWorkoutDetail(null)
    setShowProgramModal(true)
    try {
      // Toast.show({ icon: 'loading', content: 'Loading workout details...' })
      const workoutId = activity.id
      const res = await getWorkoutDetail(workoutId)

      const planned = plannedData.find(item => item.id === activity.program_item_id)

      const enrichedWorkout = {
        ...res?.workout,
        actual_intensity: activity.intensity,
        planned_intensity: planned?.intensity,
        planned_duration: planned?.duration
      }
      setProgramWorkoutDetail(enrichedWorkout)
      setIsViewOnly(true)
    } catch (err) {
      console.error('Failed to fetch workout detail:', err)
      Toast.show({ icon: 'fail', content: 'Failed to load workout details' })
    }
  }

  const handleEditProgramModal = async (activity) => {
    setProgramWorkoutDetail(null)
    setShowEditProgramModal(true)
    try {
      const workoutId = activity.id
      const res = await getWorkoutDetail(workoutId)

      const planned = plannedData.find(item => item.id === activity.program_item_id)

      const enrichedWorkout = {
        ...res?.workout,
        actual_intensity: activity.intensity,
        planned_intensity: planned?.intensity,
        planned_duration: planned?.duration
      }
      setProgramWorkoutDetail(enrichedWorkout)
      setIsManualsave(true)
      setIsViewOnly(false)
    } catch (err) {
      console.error('Failed to fetch workout detail:', err)
      Toast.show({ icon: 'fail', content: 'Failed to load workout details' })
    }
  }

  return (
    <div ref={scrollContainerRef}
    className='program-body'
      style={{
        height: '100vh',
        overflowY: 'auto'
      }}>
      <div className="programs-slide-content">
        <div className="programs-header">Program</div>
        
        <div className="programs-container healthy-signal">
          <div className="key-observations-card" style={{ paddingLeft: '4px', paddingRight: '4px'}}>
            <div className="header-row">
              <span className="exercise-header">Healthy Signals</span>
            </div>

            <div className="divider" />

            <NativeHSD hash={hash} isProgramsTab={true} />
          </div>
        </div>

        <div className="programs-container exercise">
          <div className="key-observations-card">
            <div className="header-row">
              <img src={ExerciseIcon} alt="Exercise" />
              <span className="exercise-header">Exercise</span>
            </div>
            <h4 className="key-observations-title">Key Observations</h4>
            <p
              className="observations-text truncated-text"
              onClick={() => {
                if (!exerciseSummary) return
                setShowSummaryModal(true)
              }}
            >
              {exerciseSummary || 'No observations available.'}
            </p>
          </div>
        </div>
        <Button className="add-workout-button" onClick={handleAddWorkout}>
          Add a Workout +
        </Button>
        <div className="programs-container exercise-wrapper">
          <div className="key-observations-card">
            <div className={`header-slider ${direction}`} key={activeDayIndex + '-' + direction}>
              <div className="header-text previous-day-heading" style={{ cursor: activeDayIndex > todayIndex ? 'pointer' : 'default' }}>
                {activeDayIndex > 0 && (
                  <span onClick={() => handleHeaderClick(activeDayIndex - 1)}>
                    {moment().startOf('isoWeek').add(activeDayIndex - 1, 'days').format('YYYY')}
                  </span>
                )}
              </div>

              <div className="header-text current-day-heading">
                {moment().startOf('isoWeek').add(activeDayIndex, 'days').format('D MMM YYYY')}
                <div className="display-date-divider"></div>
              </div>

              <div className="header-text next-day-heading" style={{ cursor: activeDayIndex < 6 ? 'pointer' : 'default' }}>
                {activeDayIndex < 6 && (
                  <span onClick={() => handleHeaderClick(activeDayIndex + 1)}>
                    {moment().startOf('isoWeek').add(activeDayIndex + 1, 'days').format('D MMM')}
                  </span>
                )}
              </div>
            </div>

            <Swiper
              key={todayIndex}
              loop={false}
              defaultIndex={todayIndex}
              ref={swiperRef}
              onIndexChange={handleIndexChange}
              style={{ height: '100%' }}
            >
              {daysToShow.map((index) => {
                const exerciseActivities = exerciseByDay[index] || []
                const scheduledItems = programItemsByDay[index] || []

                return (
                  <Swiper.Item key={index}>
                    <div>
                      {(exerciseActivities.length > 0 || scheduledItems.length > 0) ? (
                        <>
                          {exerciseActivities.map((activity) => (
                            <ExerciseCard
                              key={activity.id}
                              activity={activity}
                              onEdit={handleEdit}
                              onClick={handleView}
                              isAdaptive={true}
                              isProgramItem={false}
                            />
                          ))}
                          {scheduledItems.map((item) => (
                            <ExerciseCard
                              key={`program-${item.id}`}
                              activity={{
                                ...item,
                                day_of_week: index,
                                source: item.source || 'manual',
                                isProgramItem: true
                              }}
                              onEdit={handleEditProgramModal}
                              onClick={handleViewProgramItem}
                              isAdaptive={false}
                              isProgramItem={true}
                            />
                          ))}
                        </>
                      ) : (
                        <p className="no-recommendations">No exercise recommendations</p>
                      )}
                    </div>
                  </Swiper.Item>
                )
              })}
            </Swiper>

          </div>
        </div>

        <div className="programs-container sleep">
          <div className="key-observations-card">
            <div className="header-row">
              <img src={SleepIcon} alt="Sleep" style={{ width: '24px', height: '22px' }} />
            <span className="exercise-header" style={{ color: '#5c00d1'}}>Sleep</span>
            </div>

            <h4 className="key-observations-title">Key Observations</h4>
            <SleepCard recommendationsByDay={sleepByDay} sleepRecommendations={sleepSummary} />

          </div>
        </div>
      </div>

      <EditRecommendationModal
        visible={editModalVisible}
        initialData={editingActivity}
        onClose={() => {
          setEditModalVisible(false)
          setEditingActivity(null)
          setIsAddMode(false)
        }}
        onSave={handleSaveEdit}
        onDelete={handleDelete}
        readOnly={isViewOnly}
        isAddMode={isAddMode}
        defaultSelectedDateIndex={activeDayIndex}
      />

      <ProgramWorkoutDetailModal
        visible={showProgramModal}
        onClose={() => {
          setShowProgramModal(false)
          if (wasUpdatedInModal) {
            fetchLivePrograms() // faster, minimal refresh
            setWasUpdatedInModal(false)
          }
        }}
        workoutDetail={programWorkoutDetail}
        readOnly={isViewOnly}
        defaultSelectedDateIndex={activeDayIndex}
        onUpdated={() => setWasUpdatedInModal(true)} // NEW PROP
      />

      <EditProgramModal
        visible={showEditProgramModal}
        onClose={() => {
          setShowEditProgramModal(false)
          // setProgramWorkoutDetail(null)
          if (wasUpdatedInModal) {
            fetchLivePrograms() // faster, minimal refresh
            setWasUpdatedInModal(false)
          }
        }}        
        onSave={handleSaveEdit}
        onDelete={handleDelete}
        workoutDetail={programWorkoutDetail}
        readOnly={isViewOnly}
        defaultSelectedDateIndex={activeDayIndex}
        onUpdated={() => setWasUpdatedInModal(true)} // NEW PROP
      />

      <ExerciseSummaryModal
        visible={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        summary={exerciseSummary}
      />
    </div>
  )
}

export default Programs
